import * as S from '../app/state';
import { t } from '../i18n';
import { game } from '../game/client';
import { Icon, press, skillTip, consumableTip } from './common';
import { SKILLS, resolveSkill, type SkillView } from '@knight/shared';
import { setPref } from '../app/prefs';

/** Draws a stick where the finger landed, with the knob at the current tilt. */
function Stick(props: { s: S.StickView | null; kind: 'move' | 'aim' }) {
  const s = props.s;
  if (!s) return null;
  const R = 54;
  return (
    <div class={'stick ' + props.kind} style={{ left: s.ox, top: s.oy }}>
      <div class="ring" />
      <div class="knob" style={{ transform: `translate(${s.dx * R}px, ${s.dy * R}px)` }} />
    </div>
  );
}

function TouchBtn(props: { cls?: string; label: string; hint?: string; on?: boolean; badge?: string; onPress: () => void }) {
  return (
    <button class={'touch-btn' + (props.cls ? ' ' + props.cls : '') + (props.on ? ' on' : '')} aria-label={props.hint} title={props.hint} {...press(props.onPress)}>
      {props.label}
      {props.badge && <span class="badge-n">{props.badge}</span>}
    </button>
  );
}

/** Seconds left on a skill, mirrored from the HUD so the touch buttons read the same. */
function cdText(s: SkillView): string | undefined {
  if (s.cd <= 0) return undefined;
  const def = SKILLS[s.id];
  if (!def) return undefined;
  const left = s.cd * (def.cooldown ?? 5) * resolveSkill(def, s.aspects).cooldownMul;
  return left >= 10 ? String(Math.ceil(left)) : (Math.ceil(left * 10) / 10).toFixed(1);
}

/**
 * Phone controls: two thumb sticks on the canvas, the skills and potions under the right thumb,
 * and buttons for everything a keyboard would give. The left half walks, the right half aims.
 */
export function TouchControls() {
  const me = S.self.value;
  const spec = S.spectate.value;
  // a panel over the canvas owns the screen: every one of them carries its own way out
  if (S.menuOpen.value || S.buildOpen.value || S.chatOpen.value || S.deathCard.value) return null;
  const dashLeft = me?.dash.charges ?? 0;
  return (
    <>
      <Stick s={S.stickMove.value} kind="move" />
      <Stick s={S.stickAim.value} kind="aim" />
      <div class="touch-top">
        <TouchBtn cls="sq" label="≡" hint={t('ctl.menu')} onPress={() => (S.menuOpen.value = !S.menuOpen.value)} />
        {!spec && <TouchBtn cls="sq" label="▤" hint={t('ctl.build')} onPress={() => (S.buildOpen.value = !S.buildOpen.value)} />}
        <TouchBtn cls="sq" label="✉" hint={t('ctl.chat')} onPress={() => (S.chatOpen.value = !S.chatOpen.value)} />
        <TouchBtn
          cls="sq"
          label="⌖"
          hint={t('ctl.map')}
          onPress={() => {
            if (game.view) game.view.showMinimap = !game.view.showMinimap;
          }}
        />
      </div>
      {me && !spec && (
        <>
          <div class="touch-side">
            <TouchBtn cls="round dash" label="»" hint={t('ctl.dash')} on={dashLeft > 0} badge={me.dash.max > 1 ? String(dashLeft) : undefined} onPress={() => game.input.pressDash()} />
            <TouchBtn cls="round" label="✋" hint={t('ctl.interact')} onPress={() => game.input.pressInteract()} />
            <TouchBtn
              cls="round"
              label="⚔"
              hint={t('ctl.autofire')}
              on={game.input.autoFire}
              onPress={() => {
                game.input.autoFire = !game.input.autoFire;
                setPref('autoFireTouch', game.input.autoFire);
                S.showToast(t('set.autofire') + ': ' + (game.input.autoFire ? 'on' : 'off'));
              }}
            />
          </div>
          <div class="touch-cluster">
            <div class="cons-row">
              {[0, 1, 2, 3].map((i) => {
                const c = me.cons[i];
                if (!c) return null;
                return <Icon kind="cons" id={c.id} size="sm" count={c.n} tip={consumableTip(c.id, c.n)} onClick={() => game.input.pressUse(i)} />;
              })}
            </div>
            <div class="skill-grid">
              {me.actives.map((s, i) => {
                return (
                  <div class="slotwrap">
                    <Icon kind="skill" id={s.id} level={s.level} cd={s.cd} cdText={cdText(s)} tip={skillTip(s.id, s.level, s.aspects)} onClick={() => game.input.pressSkill(i)} />
                    {s.auto && (
                      <span class="autotag" title={t('label.autoCast')}>
                        A
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}
